import { useCallback, useEffect, useState } from 'react'
import { Cell } from '../Types'

// Plain, JSON-serializable stand-in for the mutable parts of a Spreadsheet
// (its Maps aren't serializable as-is), used both as the in-memory undo/redo
// stack entries and as the sessionStorage payload.
export interface SpreadsheetSnapshot {
  data: [string, Cell][]
  rows: [string, { width?: number; height?: number }][]
  number_of_columns: number
  number_of_rows: number
}

interface HistoryState {
  past: SpreadsheetSnapshot[]
  // The snapshot currently applied. Tracked here rather than re-read from
  // the spreadsheet (React Query cache) on every undo/redo, because that
  // cache is only updated once the previous undo/redo's mutation resolves —
  // reading it again for a fast follow-up click would race and desync the
  // stacks from what's actually on screen.
  current: SpreadsheetSnapshot | undefined
  future: SpreadsheetSnapshot[]
}

// Bounds sessionStorage/memory growth for very long editing sessions.
const MAX_HISTORY = 100
const EMPTY_HISTORY: HistoryState = { past: [], current: undefined, future: [] }

const storageKey = (spreadsheetId: number) =>
  `spreadsheet-history-${spreadsheetId}`

const loadHistory = (spreadsheetId: number): HistoryState => {
  const stored = sessionStorage.getItem(storageKey(spreadsheetId))
  if (!stored) return EMPTY_HISTORY

  try {
    return JSON.parse(stored) as HistoryState
  } catch {
    return EMPTY_HISTORY
  }
}

// Undo/redo history for a spreadsheet, kept in sessionStorage so it's scoped
// to this tab only (another browser/tab editing the same spreadsheet gets
// its own history rather than sharing one).
export function useSpreadsheetHistory(spreadsheetId: number | undefined) {
  const [history, setHistory] = useState<HistoryState>(EMPTY_HISTORY)

  useEffect(() => {
    setHistory(spreadsheetId ? loadHistory(spreadsheetId) : EMPTY_HISTORY)
  }, [spreadsheetId])

  const persist = useCallback(
    (next: HistoryState) => {
      setHistory(next)
      if (spreadsheetId) {
        sessionStorage.setItem(storageKey(spreadsheetId), JSON.stringify(next))
      }
    },
    [spreadsheetId],
  )

  // Records a user-driven change (as opposed to an undo/redo): `previous` is
  // the state it replaces, `next` the state it results in. Clears redo
  // history since it's no longer reachable once a new change has been made.
  const recordSnapshot = useCallback(
    (previous: SpreadsheetSnapshot, next: SpreadsheetSnapshot) => {
      persist({
        past: [...history.past, previous].slice(-MAX_HISTORY),
        current: next,
        future: [],
      })
    },
    [history, persist],
  )

  const undo = useCallback((): SpreadsheetSnapshot | undefined => {
    if (history.past.length === 0 || history.current === undefined) {
      return undefined
    }

    const previous = history.past[history.past.length - 1]
    persist({
      past: history.past.slice(0, -1),
      current: previous,
      future: [history.current, ...history.future].slice(0, MAX_HISTORY),
    })
    return previous
  }, [history, persist])

  const redo = useCallback((): SpreadsheetSnapshot | undefined => {
    if (history.future.length === 0 || history.current === undefined) {
      return undefined
    }

    const [next, ...rest] = history.future
    persist({
      past: [...history.past, history.current].slice(-MAX_HISTORY),
      current: next,
      future: rest,
    })
    return next
  }, [history, persist])

  return {
    current: history.current,
    recordSnapshot,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  }
}
